import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  server,
} from "../config";

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Form fields
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [description, setDescription] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [startPrice, setStartPrice] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [status, setStatus] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const res = await getRequest(`${server.uri}categories`);
    if (res) {
      setCategories(res);
      setAllCategories(res);
    }
  };

  const resetForm = () => {
    setName("");
    setIdentifier("");
    setDescription("");
    setIconUrl("");
    setImageUrl("");
    setCategoryType("");
    setServiceType("");
    setStartPrice("");
    setTotalCost("");
    setStatus(true);
  };

  const handleAddCategory = () => {
    resetForm();
    setEditingCategory(null);
    setOpenDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setName(category.name || "");
    setIdentifier(category.identifier || "");
    setDescription(category.description || "");
    setIconUrl(category.iconUrl || "");
    setImageUrl(category.imageUrl || "");
    setCategoryType(category.categoryType || "");
    setServiceType(category.serviceType || "");
    setStartPrice(category.startPrice || "");
    setTotalCost(category.totalCost || "");
    setStatus(category.status ?? true);
    setOpenDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      await deleteRequest(`${server.uri}categories/${id}`);
      fetchCategories();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveCategory = async () => {
    const payload = {
      name,
      identifier,
      description,
      iconUrl,
      imageUrl,
      categoryType,
      serviceType,
      startPrice: Number(startPrice),
      totalCost: Number(totalCost),
      status,
    };

    if (editingCategory) {
      await patchRequest(
        `${server.uri}categories/${editingCategory.categoryId}`,
        payload
      );
    } else {
      await postRequest(`${server.uri}categories`, payload);
    }

    fetchCategories();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allCategories.filter((cat) =>
      cat.name.toLowerCase().includes(value)
    );
    setCategories(filtered);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Manage Categories
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search by category name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: "gray", marginRight: 1 }} />,
          }}
          sx={{ width: "60%" }}
        />
        <Button variant="contained" onClick={handleAddCategory}>
          Add Category
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Service Type</strong></TableCell>
                <TableCell><strong>Start Price</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.categoryId}>
                  <TableCell>{cat.name}</TableCell>
                  <TableCell>{cat.categoryType}</TableCell>
                  <TableCell>{cat.serviceType}</TableCell>
                  <TableCell>{cat.startPrice}</TableCell>
                  <TableCell>{cat.status ? "Active" : "Inactive"}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditCategory(cat)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteCategory(cat.categoryId)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No categories found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? "Edit Category" : "Add New Category"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Description" value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Icon URL" value={iconUrl} onChange={(e) => setIconUrl(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Category Type" value={categoryType} onChange={(e) => setCategoryType(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Service Type" value={serviceType} onChange={(e) => setServiceType(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth type="number" label="Start Price" value={startPrice} onChange={(e) => setStartPrice(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth type="number" label="Total Cost" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth label="Status (true/false)" value={status} onChange={(e) => setStatus(e.target.value === "true")} sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageCategories;
